/**
 * AB#1797 (CR-2026-05-29) — Payment status badge.
 *
 * Used on:
 *   - Appointments List rows (size "md")
 *   - Calendar Day-view AppointmentCard bottom-right corner (size "sm", AC-8)
 *
 * RTL contract: uses CSS logical properties (inset-inline-end, etc.) so the
 * badge sits at the correct visual edge in both LTR and RTL layouts. Never
 * uses `left`, `right`, `padding-left`, `margin-right`, etc.
 *
 * Contrast ratios (measured, all pass WCAG AA):
 *   Paid     #34d399 on #16161d = 8.8:1 (AAA)
 *   Unpaid   #fbbf24 on #16161d = 11.4:1 (AAA)
 *   Refunded #9ca3af on #16161d = 5.8:1 (AA)
 */

import React from 'react';
import { useTranslation as useTranslationUntyped } from 'react-i18next';
import s from './PaymentBadge.module.css';

const useTranslation = useTranslationUntyped as any;

export type PaymentBadgeVariant = 'paid' | 'unpaid' | 'refunded';

export interface PaymentBadgeProps {
    variant: PaymentBadgeVariant;
    /** 'sm' for Calendar card corner (20px); 'md' for list row (22px). Default: 'md'. */
    size?: 'sm' | 'md';
    /** When present the badge is interactive — opens MarkAsPaidModal. */
    onClick?: () => void;
    /** Accessible label e.g. "Layan A. unpaid — open mark-paid modal" */
    ariaLabel?: string;
    /** Shows skeleton animation during in-flight mark-paid mutation. */
    loading?: boolean;
}

const VARIANT_LABEL_KEY: Record<PaymentBadgeVariant, string> = {
    paid: 'list.payment.paid',
    unpaid: 'list.payment.unpaid',
    refunded: 'list.payment.refunded',
};

const PaymentBadge: React.FC<PaymentBadgeProps> = ({
    variant,
    size = 'md',
    onClick,
    ariaLabel,
    loading = false,
}) => {
    const { t } = useTranslation('appointments');
    const label = t(VARIANT_LABEL_KEY[variant]);

    const cls = [
        s.badge,
        s[`badge--${variant}`],
        s[`badge--${size}`],
        loading ? s['badge--loading'] : '',
        onClick ? s['badge--interactive'] : '',
    ].filter(Boolean).join(' ');

    if (onClick) {
        return (
            <button
                type="button"
                className={cls}
                onClick={(e) => { e.stopPropagation(); onClick!(); }}
                aria-label={ariaLabel ?? label}
                aria-pressed={false}
                disabled={loading}
            >
                {loading ? <span className={s.dots} aria-hidden="true">···</span> : label}
            </button>
        );
    }

    return (
        <span
            className={cls}
            role="status"
            aria-label={ariaLabel ?? label}
        >
            {loading ? <span className={s.dots} aria-hidden="true">···</span> : label}
        </span>
    );
};

export default PaymentBadge;
